import {StatusCodes} from 'http-status-codes';
import React from 'react';
import {useDispatch} from 'react-redux';
import {
  SOURCE_AMBIENT_WEATHER,
  SOURCE_DAVIS,
  SOURCE_ECOWITT,
  SOURCE_NETATMO_WEATHER,
  SOURCE_WEATHERFLOW,
  SOURCE_WUNDERGROUND,
  WeatherService,
} from '../api';
import {StationDetailOptions} from '../api/actions/weather';
import {StationSource, WeatherStationFromDetail} from '../api/types';
import {addStation, removeStation, setAlert} from '../store';
import {Dates} from '../utils';
import useApiClient, {ErrorInfo} from './useApiClient';

export type FetchStationDetailOptions = StationDetailOptions & {
  onSuccess?: () => void;
  onError?: () => void;
};

const useFetchStationDetail = () => {
  const {fetch, loading} = useApiClient();
  const dispatch = useDispatch();

  const fetchStationDetail = React.useCallback(
    (stationId: number, options?: FetchStationDetailOptions) => {
      fetch(WeatherService.stationDetail(stationId, options), {ignore404: true})
        .then((data: WeatherStationFromDetail) => {
          dispatch(addStation(data));
          options?.onSuccess?.();
        })
        .catch((err: ErrorInfo) => {
          if (err.statusCode === StatusCodes.NOT_FOUND) {
            dispatch(removeStation(stationId));
            dispatch(
              setAlert({
                message: 'This station is not available anymore.',
                type: 'ERROR',
              }),
            );
          }
          options?.onError?.();
        });
    },
    [dispatch, fetch],
  );

  /**
   * Tells whether station requires to be updated
   *
   * It currently depends on the source and last time it has been fetched
   */
  const stationUpdateRequired = React.useCallback(
    (
      lastDataFetch: number,
      lastUpdate: number,
      source: StationSource,
      linked: boolean,
    ) => {
      const lastUpdateDelay = Dates.timeDiffUtcWithNowInSeconds(lastUpdate);
      const lastFetched = Dates.timeDiffUtcWithNowInSeconds(lastDataFetch);
      let delay = 120;
      if (linked) {
        if (source === SOURCE_WUNDERGROUND) {
          delay = 300;
        } else if (
          [
            SOURCE_AMBIENT_WEATHER,
            SOURCE_WEATHERFLOW,
            SOURCE_ECOWITT,
            SOURCE_DAVIS,
          ].includes(source)
        ) {
          delay = 70;
        } else if (source === SOURCE_NETATMO_WEATHER) {
          delay = 600;
        }
      }
      // Refetch only when a new measurement is plausibly available (the one we
      // hold is older than the source cadence) AND we have not already polled
      // within that cadence. Gating the lower bound on `delay` rather than a
      // fixed 20s stops a public/non-linked or stale station — whose `lastUpdate`
      // is permanently older than `delay` — from being re-fetched every ~20s for
      // as long as the dashboard stays mounted. That refetch storm drove the
      // Reanimated/worklet heap-corruption crashes SMARTMIXIN-IOS-61..6B (1.56.0).
      if (lastUpdateDelay >= delay && lastFetched >= delay) {
        // Last Station detail is outdated, update required
        return true;
      }
      return false;
    },
    [],
  );

  return React.useMemo(
    () => ({
      fetchStationDetail,
      stationUpdateRequired,
      loading,
    }),
    [fetchStationDetail, loading, stationUpdateRequired],
  );
};

export default useFetchStationDetail;
